// frontend/src/components/report/PlanTab.js
import { useState, useRef } from "react";
import { Rocket, CheckCircle2, Info, BookOpen, ListChecks } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { SEVERITY_CLS } from "./reportUtils";

const WIDE_MONTHS = [
  { key: "month_1", n: 1 },
  { key: "month_2", n: 2 },
  { key: "month_3", n: 3 },
];

// Actions were plain strings before 2026-09-14; older, already-approved
// reports still have that shape in the database and must keep rendering —
// only newly generated ones carry {text, priority}.
const actionText = (a) => (typeof a === "string" ? a : a.text);
const actionPriority = (a) => (typeof a === "string" ? null : a.priority);

// General strategy education, not a measurement of this business — kept
// visually and structurally separate from the personalized plan above it
// (owner request 2026-09-30). Collapsed by default: it's background reading,
// not the primary content of this tab. German-market specific (GDPR, German
// directories) — only defined on T.de, so its absence on T.en is the gate;
// no separate `lang` check needed here.
function StrategyBackground({ L }) {
  if (!L.strategyTitle) return null;
  return (
    <Accordion type="single" collapsible className="mt-6 print:hidden">
      <AccordionItem value="strategy" className="overflow-hidden rounded-2xl border bg-card shadow-sm [&>h3]:m-0">
        <AccordionTrigger className="px-5 py-4 text-left hover:no-underline sm:px-6">
          <span className="flex items-center gap-1.5 font-heading text-base font-semibold">
            <BookOpen className="h-4 w-4 shrink-0" /> {L.strategyTitle}
          </span>
        </AccordionTrigger>
        <AccordionContent className="px-5 pb-5 sm:px-6">
          <p className="text-base text-muted-foreground">{L.strategyIntro}</p>
          <div className="mt-5 space-y-5">
            {L.strategySections.map((s) => (
              <div key={s.heading}>
                <h3 className="text-base font-semibold">{s.heading}</h3>
                {s.paragraphs.map((p, i) => (
                  <p key={i} className="mt-1.5 text-base text-muted-foreground">{p}</p>
                ))}
                {s.items && (
                  <ul className="mt-2 list-disc space-y-1.5 pl-5 text-base text-muted-foreground">
                    {s.items.map((item, i) => <li key={i}>{item}</li>)}
                  </ul>
                )}
                {s.note && <p className="mt-2 text-base italic text-muted-foreground">{s.note}</p>}
              </div>
            ))}
          </div>
          <div className="mt-5 rounded-xl border bg-secondary/30 p-4">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{L.strategyBudgetTitle}</h3>
            <ul className="mt-2 space-y-1 text-base">
              {L.strategyBudgetItems.map((b) => (
                <li key={b.label} className="flex items-baseline gap-2">
                  <span className="w-12 shrink-0 text-right font-semibold tabular-nums">{b.pct}%</span>
                  <span className="text-muted-foreground">{b.label}</span>
                </li>
              ))}
            </ul>
          </div>
          <p className="mt-5 text-base text-muted-foreground">{L.strategyConclusion}</p>
          <p className="mt-3 text-xs text-muted-foreground">{L.strategySource}</p>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

export default function PlanTab({ data, L }) {
  const wideReport = data.wide_report;
  const [activeMonth, setActiveMonth] = useState(1);
  const refs = useRef({});

  if (!wideReport) {
    return (
      <>
        <div className="rounded-2xl border bg-card p-5 text-base text-muted-foreground shadow-sm sm:p-6">{L.planEmptyState}</div>
        <StrategyBackground L={L} />
      </>
    );
  }

  const months = WIDE_MONTHS.filter(({ key }) => wideReport[key]);

  const scrollTo = (n) => {
    setActiveMonth(n);
    refs.current[n]?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  };

  return (
    <>
      <div className="mt-6 overflow-hidden rounded-2xl border border-primary/30 bg-card shadow-sm">
      <div className="border-b bg-accent/40 p-5 sm:p-6">
        <h2 className="mb-1.5 flex items-center gap-1.5 font-heading text-lg font-semibold"><Rocket className="h-4 w-4" /> {wideReport.headline}</h2>
        <p className="text-base text-muted-foreground">{wideReport.summary}</p>
      </div>

      {months.length > 1 && (
        <div className="flex items-center gap-2 border-b bg-secondary/30 px-5 py-3 sm:px-6">
          {months.map(({ n }, i) => (
            <div key={n} className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => scrollTo(n)}
                aria-label={L.monthLabel ? L.monthLabel(n) : `Go to month ${n}`}
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-bold tabular-nums transition-colors ${
                  activeMonth === n ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:bg-secondary/80"
                }`}
              >
                {n}
              </button>
              {i < months.length - 1 && <div className="h-px w-6 bg-border" />}
            </div>
          ))}
        </div>
      )}

      <div className="space-y-5 p-5 sm:p-6">
        {months.map(({ key, n }) => {
          const m = wideReport[key];
          return (
            <div key={key} ref={(el) => (refs.current[n] = el)}>
              <div className="mb-2 flex items-center gap-2">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold tabular-nums text-primary-foreground">{n}</span>
                <h3 className="text-base font-semibold">{m.theme}</h3>
              </div>
              <ul className="space-y-1.5 pl-8">
                {(m.actions || []).map((a, i) => {
                  const priority = actionPriority(a);
                  return (
                    <li key={i} className="flex items-start gap-2 text-base text-muted-foreground">
                      <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                      <span className="flex-1">
                        {priority && (
                          <span className={`mr-1.5 rounded-full border px-1.5 py-0.5 text-xs font-semibold ${SEVERITY_CLS[priority] || SEVERITY_CLS.low}`}>
                            {L.severity[priority] || priority}
                          </span>
                        )}
                        {actionText(a)}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}

        {wideReport.additional_notes?.length > 0 && (
          <div className="border-t pt-4">
            <h3 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-muted-foreground">
              <Info className="h-3.5 w-3.5" /> {L.additionalNotesTitle}
            </h3>
            <p className="mb-2 text-sm text-muted-foreground">{L.additionalNotesDesc}</p>
            <ul className="space-y-1.5 pl-1">
              {wideReport.additional_notes.map((note, i) => (
                <li key={i} className="rounded-lg bg-secondary/50 p-2.5 text-sm text-muted-foreground">{note}</li>
              ))}
            </ul>
          </div>
        )}

        {wideReport.comprehensive_guidance?.length > 0 && (
          <div className="border-t pt-4">
            <h3 className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-muted-foreground">
              <ListChecks className="h-3.5 w-3.5" /> {L.comprehensiveGuidanceTitle}
            </h3>
            <p className="mb-3 text-sm text-muted-foreground">{L.comprehensiveGuidanceDesc}</p>
            <div className="space-y-4">
              {wideReport.comprehensive_guidance.map((g, i) => (
                <div key={i} className="rounded-xl border bg-secondary/30 p-4">
                  <h4 className="text-base font-semibold">{g.gap_title}</h4>
                  {g.intro && <p className="mt-1 text-sm text-muted-foreground">{g.intro}</p>}
                  <ul className="mt-3 space-y-2">
                    {(g.recommendations || []).map((r, j) => (
                      <li key={j} className="flex items-start gap-2 text-base text-muted-foreground">
                        <span className="mt-0.5 shrink-0 rounded-full border px-1.5 py-0.5 text-xs font-semibold text-muted-foreground">
                          {r.category}
                        </span>
                        <span className="flex-1">{r.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
    <StrategyBackground L={L} />
    </>
  );
}
